import { useEffect } from "react";

interface MetaTagsOptions {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: "website" | "article";
  siteName?: string;
  twitterCard?: "summary" | "summary_large_image";
  publishedTime?: string;
  tags?: string[];
}

const DEFAULT_SITE_NAME = "VEA STUDIO";
const DEFAULT_DESCRIPTION =
  "VEA STUDIO - Vidéaste professionnelle & Pilote de drone certifiée à Bordeaux. Mariages, immobilier, entreprises, associations. Rayon 75 km autour de Bordeaux.";
const DEFAULT_IMAGE = "https://veastudio.fr/og-veastudio.png";
const DEFAULT_URL = "https://veastudio.fr";

function setMeta(property: string, content: string, isName = false) {
  const attr = isName ? "name" : "property";
  let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Hook pour injecter dynamiquement les balises Open Graph et Twitter Card dans le <head>.
 * Utilisé sur les pages dont le contenu change selon les données (articles de blog, pages villes, etc.).
 */
export function useMetaTags(options: MetaTagsOptions) {
  const {
    title,
    description = DEFAULT_DESCRIPTION,
    image = DEFAULT_IMAGE,
    url = DEFAULT_URL,
    type = "website",
    siteName = DEFAULT_SITE_NAME,
    twitterCard = "summary_large_image",
    publishedTime,
    tags = [],
  } = options;

  useEffect(() => {
    const fullTitle = title ? `${title} | VEA STUDIO` : "VEA STUDIO - Vidéaste & Drone Bordeaux";

    // Titre de la page
    document.title = fullTitle;

    // Meta description standard
    setMeta("description", description, true);

    // Open Graph - obligatoires
    setMeta("og:title", fullTitle);
    setMeta("og:description", description);
    setMeta("og:image", image);
    setMeta("og:url", url);
    setMeta("og:type", type);
    setMeta("og:site_name", siteName);
    setMeta("og:locale", "fr_FR");

    // Open Graph - image dimensions pour un meilleur rendu
    setMeta("og:image:width", "1200");
    setMeta("og:image:height", "630");
    setMeta("og:image:alt", fullTitle);

    // Open Graph - article spécifique
    if (type === "article" && publishedTime) {
      setMeta("article:published_time", publishedTime);
      setMeta("article:author", "Valentine Baumberger");
      setMeta("article:publisher", "https://veastudio.fr");
      tags.forEach((tag) => {
        const el = document.createElement("meta");
        el.setAttribute("property", "article:tag");
        el.setAttribute("content", tag);
        document.head.appendChild(el);
      });
    }

    // Twitter Card
    setMeta("twitter:card", twitterCard, true);
    setMeta("twitter:title", fullTitle, true);
    setMeta("twitter:description", description, true);
    setMeta("twitter:image", image, true);
    setMeta("twitter:site", "@VEAStudio", true);
    setMeta("twitter:creator", "@VEAStudio", true);

    // URL canonique
    setLink("canonical", url);

    // Nettoyage : restaurer les valeurs par défaut au démontage
    return () => {
      document.title = "VEA STUDIO - Vidéaste & Drone Bordeaux";
    };
  }, [title, description, image, url, type, siteName, twitterCard, publishedTime, tags]);
}
